import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../data-access/auth.service';

// Public user endpoints that authenticate the request themselves and must NOT
// wait for / attach a CSRF token (no session exists yet). Substring match covers
// the variants (e.g. `/user/login/2fa`, `/user/register/verify`).
const CSRF_EXCLUDE_PATHS = ['/user/login', '/user/register', '/user/password/reset'];

const UNSAFE_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

/**
 * Auth interceptor for the cookie-session + double-submit CSRF flow.
 *
 * Auth rides the httpOnly user session cookie (no Bearer header). Every API call
 * sends credentials so the browser returns that cookie; mutating requests echo the
 * in-memory CSRF token in the `x-csrf-token` header. On 401 the session is cleared
 * and the user is sent to login.
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const isApiRequest = req.url.startsWith(environment.apiBaseUrl);

  // Leave non-API requests (assets, etc.) untouched.
  if (!isApiRequest) {
    return next(req);
  }

  // Always send credentials so the session + CSRF cookies flow across origins.
  // getAccessToken() returns null in production, when logged out, and once the
  // token expires — so the Bearer header is a dev-only, in-memory fallback and an
  // expired token is never attached. Production authenticates via the cookie alone.
  const accessToken = authService.getAccessToken();
  const baseReq = req.clone({
    withCredentials: true,
    ...(accessToken ? { setHeaders: { Authorization: `Bearer ${accessToken}` } } : {}),
  });

  // Cookie-only variant (no Bearer) used to retry a 401 once: the stored token may
  // be stale/revoked while the httpOnly session cookie is still valid.
  const cookieOnlyReq = req.clone({ withCredentials: true });

  const isPublic = CSRF_EXCLUDE_PATHS.some((path) => req.url.includes(path));
  const needsCsrf = !isPublic && UNSAFE_METHODS.includes(req.method);

  const withCsrf = (request: HttpRequest<unknown>): HttpRequest<unknown> => {
    const csrf = authService.csrfToken();
    if (needsCsrf && csrf) {
      return request.clone({ setHeaders: { 'x-csrf-token': csrf } });
    }
    return request;
  };

  const onUnauthorized = (): void => {
    // A 401 from a public auth endpoint (login / 2FA verify / 2FA resend /
    // register / password reset) is a form-level error — bad credentials or an
    // invalid/expired code — NOT an expired session. Leave the user on the page so
    // it can surface the message; don't tear down auth or bounce to /auth/login.
    if (isPublic) {
      return;
    }
    authService.clearAuth();
    if (!router.url.includes('/auth/login')) {
      router.navigate(['/auth/login']);
    }
  };

  // The in-memory CSRF token is fetched once and never re-checked — on a long-lived tab the
  // signed cookie it's validated against can rotate out from under it (session refreshed
  // elsewhere, cookie re-issued, TTL passed), and the backend 403s with this exact message.
  // Recover by forcing a fresh token and retrying once, rather than surfacing that as whatever
  // unrelated error the calling page happens to show for a plain 403.
  const isCsrfInvalid = (error: unknown): boolean =>
    error instanceof HttpErrorResponse &&
    error.status === 403 &&
    (error.error as { metadata?: { message?: string } } | null)?.metadata?.message === 'user.csrf.invalid';

  const send = (retriedCsrf = false): Observable<HttpEvent<unknown>> => {
    return next(withCsrf(baseReq)).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          // If the request carried a Bearer header, the token may simply be stale
          // while the cookie session is fine — retry once cookie-only before
          // tearing down auth.
          if (accessToken) {
            return next(withCsrf(cookieOnlyReq)).pipe(
              catchError((retryError: unknown) => {
                if (
                  retryError instanceof HttpErrorResponse &&
                  retryError.status === 401
                ) {
                  onUnauthorized();
                }
                return throwError(() => retryError);
              })
            );
          }
          onUnauthorized();
        }
        if (needsCsrf && !retriedCsrf && isCsrfInvalid(error)) {
          return from(authService.refreshCsrfToken()).pipe(switchMap(() => send(true)));
        }
        return throwError(() => error);
      })
    );
  };

  // Block a mutating request until the CSRF token is in memory (e.g. right after
  // a page reload, when only the httpOnly cookie survives). If the bootstrap
  // fails — or completes without a usable token — fail the request rather than
  // sending it unprotected.
  if (needsCsrf && !authService.csrfToken()) {
    return from(authService.ensureCsrfReady()).pipe(
      switchMap(() => {
        if (!authService.csrfToken()) {
          return throwError(
            () => new Error('CSRF token unavailable; mutating request blocked.')
          );
        }
        return send();
      }),
      catchError((error: unknown) => {
        // An expired session surfaces here as a 401 on /csrf-token: clear local
        // state and bounce to login, mirroring send()'s 401 handling.
        if (error instanceof HttpErrorResponse && error.status === 401) {
          onUnauthorized();
        }
        return throwError(() => error);
      })
    );
  }

  return send();
};
